import React, { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Edit3, Eye, ImagePlus, Layers, Plus, RefreshCw, Send, Sparkles, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fresh = () => ({ title:"", description:"", level:"Iniciático", energy_cost:0, duration:"", cover_url:"", preview_video_url:"", instructor:"", lesson_title:"", content_url:"", summary:"" });
const labels = { draft:"Borrador", pending_review:"En revisión", approved:"Publicado", rejected:"Cambios solicitados" };

const toPayload = (form) => ({
  title: form.title,
  description: form.description,
  level: form.level,
  price: 0,
  energy_cost: Number(form.energy_cost) || 0,
  duration: form.duration,
  cover_url: form.cover_url || null,
  preview_video_url: form.preview_video_url || null,
  instructor: form.instructor,
  requirements: [],
  outcomes: [],
  featured: false,
  landing_visible: false,
  active: false,
  modules: form.lesson_title ? [{
    id: "modulo-inicial",
    title: "Módulo inicial",
    lessons: [{ id:"leccion-inicial", title:form.lesson_title, duration_min:0, content_url:form.content_url || null, material_url:null, summary:form.summary }]
  }] : [],
  materials: [],
  exam: { enabled:false, pass_percent:85, max_attempts:3, questions:[] },
});

const fromCourse = (course) => {
  const lesson = course.modules?.[0]?.lessons?.[0] || {};
  return { ...fresh(), ...course, energy_cost:Number(course.energy_cost || 0), lesson_title:lesson.title || "", content_url:lesson.content_url || "", summary:lesson.summary || "" };
};

const CreatorAcademy = () => {
  const fileRef = useRef(null);
  const [items,setItems] = useState([]);
  const [form,setForm] = useState(fresh());
  const [editing,setEditing] = useState(null);
  const [busy,setBusy] = useState("");
  const [notice,setNotice] = useState({type:"",text:""});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/creator/academy/courses");
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      setNotice({ type:"error", text:formatApiError(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      if (editing === "new") await api.post("/api/creator/academy/courses", toPayload(form));
      else await api.put(`/api/creator/academy/courses/${editing}`, toPayload(form));
      setNotice({ type:"ok", text:"Borrador académico guardado." });
      setEditing(null);
      await load();
    } catch (error) {
      setNotice({ type:"error", text:formatApiError(error) });
    } finally {
      setBusy("");
    }
  };

  const submit = async (item) => {
    setBusy(item.slug);
    try {
      await api.post(`/api/creator/academy/courses/${item.slug}/submit`);
      setNotice({ type:"ok", text:"Contenido enviado a Gobierno Académico para revisión." });
      await load();
    } catch (error) {
      setNotice({ type:"error", text:formatApiError(error) });
    } finally {
      setBusy("");
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/api/creator/marketplace/media", body, { headers:{ "Content-Type":"multipart/form-data" } });
      setForm((value) => ({ ...value, cover_url:data.url }));
    } catch (error) {
      setNotice({ type:"error", text:formatApiError(error) });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-[1450px] pb-12" data-testid="creator-academy-page">
      <section className="relative mb-6 overflow-hidden rounded-[30px] border border-[#d4ab5e]/20 bg-[#0d0b08] p-6 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(212,171,94,.14),transparent_34%)]" />
        <div className="relative flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div>
            <div className="flex items-center gap-2 font-mont text-[8px] uppercase tracking-[.3em] text-[#aa956e]"><Sparkles size={12}/> Constructor Académico KD9</div>
            <h1 className="mt-2 font-cinzel text-3xl text-[#f1dcae] md:text-4xl">Construye el sendero</h1>
            <p className="mt-3 max-w-3xl font-cormorant text-xl text-[#b3a895]">Crea material en borrador, previsualiza su estructura y envíalo a revisión. El Creador nunca publica directamente.</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 self-start rounded-xl border border-white/10 px-4 py-2.5 font-mont text-[9px] uppercase tracking-[.14em] text-[#bbaa8b]"><RefreshCw size={12}/> Actualizar</button>
        </div>
      </section>

      {notice.text && <div className={`mb-5 rounded-2xl border p-4 font-mont text-sm ${notice.type === "error" ? "border-red-400/20 bg-red-950/20 text-red-200" : "border-emerald-400/20 bg-emerald-950/15 text-emerald-200"}`}>{notice.text}</div>}

      {editing ? (
        <section className="mb-6 rounded-[28px] border border-white/9 bg-[#0f0d0a] p-5 md:p-7">
          <div className="mb-6 flex items-start justify-between gap-3">
            <div>
              <div className="font-mont text-[8px] uppercase tracking-[.25em] text-[#8f8169]">{editing === "new" ? "Nuevo borrador" : "Edición de contenido"}</div>
              <h2 className="mt-1 font-cinzel text-2xl text-[#ead3a3]">{editing === "new" ? "Crear contenido académico" : form.title}</h2>
            </div>
            <button onClick={() => setEditing(null)} className="rounded-xl border border-white/10 p-2 text-[#a99d89]"><X size={18}/></button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {[["title","Título","text"],["level","Nivel","text"],["energy_cost","Costo en Energía KD9","number"],["duration","Duración","text"],["instructor","Instructor","text"],["preview_video_url","Video de presentación","text"],["lesson_title","Título de la primera lección","text"],["content_url","Enlace del contenido","text"]].map(([key,label,type]) => (
              <label key={key} className="font-mont text-[8px] uppercase tracking-[.16em] text-[#948873]">
                {label}
                <input type={type} min={type === "number" ? 0 : undefined} className="kd9-input mt-2 pl-3" value={form[key] ?? ""} onChange={(event) => setForm({ ...form, [key]:event.target.value })}/>
              </label>
            ))}
            <label className="md:col-span-2 font-mont text-[8px] uppercase tracking-[.16em] text-[#948873]">Descripción
              <textarea rows={5} className="kd9-input mt-2 p-3" value={form.description} onChange={(event) => setForm({ ...form, description:event.target.value })}/>
            </label>
            <label className="md:col-span-2 font-mont text-[8px] uppercase tracking-[.16em] text-[#948873]">Resumen de la primera lección
              <textarea rows={3} className="kd9-input mt-2 p-3" value={form.summary} onChange={(event) => setForm({ ...form, summary:event.target.value })}/>
            </label>

            <div className="md:col-span-2">
              <div className="font-mont text-[8px] uppercase tracking-[.16em] text-[#948873]">Portada</div>
              {form.cover_url ? (
                <div className="relative mt-2 h-48 max-w-md overflow-hidden rounded-2xl border border-white/10">
                  <img src={form.cover_url} alt="Portada" className="h-full w-full object-cover"/>
                  <button onClick={() => setForm({ ...form, cover_url:"" })} className="absolute right-2 top-2 rounded-full bg-black/75 p-2"><X size={14}/></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} className="mt-2 inline-flex items-center gap-2 rounded-xl border border-[#d4ab5e]/30 px-4 py-3 font-mont text-[9px] uppercase tracking-[.13em] text-[#dfc17f]"><ImagePlus size={14}/>{busy === "upload" ? "Subiendo…" : "Subir portada"}</button>
              )}
              <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(event) => uploadCover(event.target.files?.[0])}/>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button disabled={busy === "save"} onClick={save} className="rounded-xl bg-[#d4ab5e] px-5 py-3 font-mont text-[9px] font-bold uppercase tracking-[.14em] text-[#171008]">{busy === "save" ? "Guardando…" : "Guardar borrador"}</button>
            <button onClick={() => setEditing(null)} className="rounded-xl border border-white/10 px-5 py-3 font-mont text-[9px] uppercase tracking-[.14em] text-[#b7a98d]">Cancelar</button>
          </div>
        </section>
      ) : (
        <button onClick={() => { setForm(fresh()); setEditing("new"); }} className="mb-6 inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#d4ab5e] px-5 font-mont text-[9px] font-bold uppercase tracking-[.14em] text-[#171008]"><Plus size={13}/> Nuevo contenido</button>
      )}

      {items.length ? (
        <div className="grid gap-5 lg:grid-cols-2">
          {items.map((item) => (
            <article key={item.slug} className="overflow-hidden rounded-[26px] border border-white/9 bg-[#100d09]">
              {item.cover_url ? <img src={item.cover_url} alt="" className="h-44 w-full object-cover"/> : <div className="flex h-44 items-center justify-center bg-[radial-gradient(circle_at_center,rgba(212,171,94,.16),transparent_58%)] text-[#b99a5e]"><BookOpen size={38}/></div>}
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-cinzel text-xl text-[#ead5aa]">{item.title}</h2>
                    <div className="mt-2 flex flex-wrap gap-3 font-mont text-[8px] text-[#8c8272]"><span><Layers size={11} className="mr-1 inline"/>{item.modules_count} módulos</span><span>{item.lessons_count} lecciones</span><span>⚡ {item.energy_cost || 0} KD</span></div>
                  </div>
                  <span className="rounded-full border border-[#d4ab5e]/20 px-3 py-1.5 font-mont text-[7px] uppercase tracking-[.14em] text-[#c9ae76]">{labels[item.status] || item.status}</span>
                </div>
                {item.review_notes && <p className="mt-4 rounded-xl border border-white/8 bg-white/[.02] p-3 font-cormorant text-base text-[#b8ac98]">Revisión: {item.review_notes}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {["draft","rejected"].includes(item.status) && <>
                    <button onClick={() => { setForm(fromCourse(item)); setEditing(item.slug); }} className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#b9aa8d]"><Edit3 size={11}/> Editar</button>
                    <button disabled={busy === item.slug} onClick={() => submit(item)} className="inline-flex items-center gap-1 rounded-lg border border-[#d4ab5e]/30 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#dfc17f]"><Send size={11}/> Enviar a revisión</button>
                  </>}
                  <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 font-mont text-[8px] uppercase tracking-[.12em] text-[#918570]" title="La vista previa completa usa la misma estructura del estudiante"><Eye size={11}/> Vista estructural</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="rounded-[28px] border border-white/8 bg-white/[.025] p-10 text-center">
          <BookOpen className="mx-auto text-[#b99a5e]"/>
          <p className="mt-3 font-cormorant text-lg text-[#aaa08d]">Todavía no has construido contenidos académicos.</p>
        </div>
      )}
    </div>
  );
};

export default CreatorAcademy;
